import Navbar from "./Navbar";
import Footer from "./Footer";
import { useSEO } from "../hooks/useSEO";
import { Reveal } from "./Reveal";
import termsData from "../data/migrated/terms-text.json";

// Parse the genuine old-site T&C text into intro + numbered sections with subsections
const parseTerms = (raw) => {
  let text = raw.replace(/^Terms & Conditions\s*/, "");
  const junkIdx = text.search(/\sPAGES\s+HOME\s/);
  if (junkIdx !== -1) text = text.slice(0, junkIdx);
  text = text.replace(/\s*---\s*/g, " ").trim();
  // closing note is rendered separately below the sections
  text = text.replace(/\s*By entering into a partnership with Skope Kitchens, you agree to adhere to these Terms and Conditions\..*$/, "").trim();

  const firstSection = text.search(/\s1\.\s/);
  const intro = firstSection === -1 ? text : text.slice(0, firstSection).trim();
  const body = firstSection === -1 ? "" : text.slice(firstSection);

  const sections = [];
  const sectionRe = /(?:^|\s)(\d{1,2})\.\s+([A-Z][^]*?)(?=\s\d{1,2}\.\s+[A-Z]|$)/g;
  let m;
  while ((m = sectionRe.exec(body)) !== null) {
    const content = m[2].trim();
    const subRe = /(\d+\.\d+\.)\s+([^:]+):\s*/g;
    const subs = [];
    let last = 0;
    let sm;
    const matches = [...content.matchAll(subRe)];
    matches.forEach((mm, i) => {
      const start = mm.index + mm[0].length;
      const end = i + 1 < matches.length ? matches[i + 1].index : content.length;
      subs.push({ num: mm[1], label: mm[2].trim(), body: content.slice(start, end).trim() });
      last = end;
    });
    sections.push({ num: m[1], title: subs.length ? content.slice(0, matches[0].index).trim() : content, subs });
  }
  return { intro, sections };
};

const { intro, sections } = parseTerms(termsData.text);

const TermsPage = () => {
  useSEO({
    title: "Terms & Conditions | Skope Kitchens",
    description:
      "The terms and conditions governing partnerships with Skope Kitchens — services, revenue share, responsibilities, intellectual property and dispute resolution.",
    canonical: "https://www.skopekitchens.com/terms-con.",
  });

  return (
    <div className="bg-white min-h-screen text-ink font-body" data-testid="terms-page">
      <Navbar />
      <main className="pb-24">
        <div className="bg-ink bg-blueprint text-white pt-32 lg:pt-40 pb-12 lg:pb-16">
          <div className="max-w-[860px] mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-skope uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-skope" />
                Legal
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display font-bold text-3xl lg:text-5xl tracking-tight leading-[1.1] text-white" data-testid="page-h1">
                Terms &amp; Conditions
              </h1>
            </Reveal>
          </div>
        </div>

        <div className="max-w-[860px] mx-auto px-6 mt-12" data-testid="terms-content">
          {intro && (
            <p className="text-ink/60 leading-relaxed text-base lg:text-lg">{intro.replace(/^Join Us\s*/, "")}</p>
          )}
          {sections.map((s) => (
            <section key={s.num} className="mt-12" data-testid={`terms-section-${s.num}`}>
              <h2 className="font-display font-semibold text-2xl lg:text-3xl text-ink tracking-tight">
                <span className="text-skope mr-3">{s.num}.</span>
                {s.title}
              </h2>
              <div className="mt-4 space-y-4">
                {s.subs.map((sub) => (
                  <p key={sub.num} className="text-ink/60 leading-relaxed">
                    <span className="font-semibold text-ink/85">
                      {sub.num} {sub.label}:
                    </span>{" "}
                    {sub.body}
                  </p>
                ))}
              </div>
            </section>
          ))}
          <p className="mt-14 pt-8 border-t border-ink/10 text-ink/50 text-sm leading-relaxed">
            By entering into a partnership with Skope Kitchens, you agree to adhere to these Terms and Conditions.
            For any queries or concerns, please contact us at{" "}
            <a href="mailto:contact@skopekitchens.com" className="text-skope hover:underline" data-testid="terms-contact-link">
              contact@skopekitchens.com
            </a>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;
